import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";

import Wordmark from "./Wordmark";
import { SITE } from "@/lib/constants";
import { t } from "@/lib/i18n";

/**
 * NoJo Enterprise footer — owner directive (Feb 2026): condensed.
 *
 * Previous layout used three vertically-stacked bands (brand+contact,
 * sitemap, disclaimer/utility) with generous padding, which left a lot
 * of unused space. This version compresses everything into:
 *
 *   1. One 12-col directory row: brand & tagline + 4 sitemap columns
 *   2. One slim contact strip: phone · email · office
 *   3. One tight bottom band: disclaimer + copyright + utility chips
 */
export default function Footer({ lang }) {
  const isJa = lang === "ja";
  const year = new Date().getFullYear();
  const f = (k) => t(lang, `footer.${k}`);

  // Footer sitemap — split the formerly-tall "Company" column into two
  // shorter sub-columns ("Company" + "People") so the directory row is
  // visually balanced and the NoJo Enterprise brand block stops looking
  // floating-too-high relative to the directory.
  const cols = [
    {
      key: "company",
      titleEn: "Company",
      titleJa: "会社情報",
      links: [
        { label: isJa ? "会社情報" : "About Us", path: "/about" },
        { label: isJa ? "テクノロジー" : "Technology", path: "/technology" },
        { label: isJa ? "信頼とセキュリティ" : "Trust & Security", path: "/trust-security" },
        { label: isJa ? "会社概要" : "Corporate Profile", path: "/corporate-profile" },
        { label: isJa ? "採用情報" : "Careers", path: "/careers" },
      ],
    },
    {
      key: "people",
      titleEn: "People",
      titleJa: "メンバー",
      links: [
        { label: isJa ? "AIチーム" : "Our AI Team", path: "/our-team" },
        { label: isJa ? "リーダーシップチーム" : "Leadership Team", path: "/our-leaders" },
        { label: isJa ? "代表メッセージ (JEC)" : "Founder's Message · JEC", path: "/founders-message/japanetccard" },
        { label: isJa ? "代表メッセージ (JTR)" : "Founder's Message · JTR", path: "/founders-message/japantollreceipts" },
      ],
    },
    {
      key: "updates",
      titleEn: "Updates",
      titleJa: "更新情報",
      links: [
        { label: isJa ? "最新情報" : "What's New", path: "/whats-new" },
        { label: isJa ? "ロードマップ" : "Roadmap", path: "/roadmap" },
        { label: isJa ? "お客様の声" : "Testimonials", path: "/testimonials" },
        { label: isJa ? "看板掲出" : "Signboards", path: "/signboards" },
      ],
    },
    {
      key: "services",
      titleEn: "Services",
      titleJa: "サービス",
      links: [
        { label: isJa ? "サービス一覧" : "All services", path: "/services" },
        { label: "JapanETCcard", path: "/services/japanetccard" },
        { label: "JapanTollReceipts", path: "/services/japantollreceipts" },
        { label: "JapanChangeMoney", path: "/services/japanchangemoney" },
      ],
    },
    {
      key: "support",
      titleEn: "Support",
      titleJa: "サポート",
      links: [
        { label: isJa ? "お問い合わせ" : "Contact", path: "/contact" },
        { label: isJa ? "プライバシーポリシー" : "Privacy Policy", path: "/legal/privacy" },
        { label: isJa ? "利用規約" : "Terms of Service", path: "/legal/terms" },
        { label: isJa ? "サイトマップ" : "Sitemap", path: "/sitemap" },
        {
          label: isJa ? "特商法に基づく表記" : "Commercial Transactions",
          path: "/legal/commercial-transactions",
        },
      ],
    },
  ];

  return (
    <footer data-testid="site-footer" className="bg-[#0A192F] text-white mt-auto">
      {/* Tri-color top accent */}
      <div className="flex h-1">
        <span className="flex-1 bg-[#5B2E83]" />
        <span className="flex-1 bg-[#2F8F3C]" />
        <span className="flex-1 bg-[#0E5FB0]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-12 pb-6 md:pb-8">
        {/* ===== ROW 1 — brand (4) + 4 sitemap columns (8) ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-8 pb-8 border-b border-white/10">
          <div className="lg:col-span-4">
            <Wordmark size="footer" tone="white" showSub={true} />
            <p
              data-testid="footer-tagline"
              className={[
                isJa ? "font-ja" : "font-display",
                "mt-4 text-[13.5px] leading-[1.7] text-white/95 max-w-md font-medium",
              ].join(" ")}
            >
              {f("supportLine")}
            </p>
            <p className="mt-2 text-[12px] leading-[1.7] text-[#CBD5E1] max-w-md">
              {f("tagline")}
            </p>
          </div>

          <div
            className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-7"
            data-testid="footer-sitemap"
          >
            {cols.map((c) => (
              <div key={c.key} data-testid={`footer-col-${c.key}`}>
                <div className="text-[10.5px] font-bold tracking-[0.16em] uppercase text-[#7AB8E8] mb-3">
                  {isJa ? c.titleJa : c.titleEn}
                </div>
                <ul className="space-y-2">
                  {c.links.map((l) => (
                    <li key={l.path}>
                      {l.external ? (
                        <a
                          href={l.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`footer-link-${l.path.replace(/\W+/g, "-")}`}
                          className="group inline-flex items-center gap-1.5 text-[12.5px] leading-[1.5] text-[#E2E8F0] hover:text-white transition-colors"
                        >
                          {l.label}
                          <ArrowUpRight
                            className="h-3 w-3 opacity-60 group-hover:opacity-100 transition-all"
                            strokeWidth={2}
                          />
                        </a>
                      ) : (
                        <Link
                          to={`/${lang}${l.path}`}
                          data-testid={`footer-link-${l.path.replace(/\W+/g, "-")}`}
                          className="group inline-flex items-center gap-1.5 text-[12.5px] leading-[1.5] text-[#E2E8F0] hover:text-white transition-colors"
                        >
                          {l.label}
                          <ArrowUpRight
                            className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                            strokeWidth={2}
                          />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ===== ROW 2 — slim contact strip (3 inline) ===== */}
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 py-6 border-b border-white/10">
          <li className="flex items-start gap-2.5" data-testid="footer-contact-phone">
            <Phone className="h-3.5 w-3.5 mt-1 text-[#7AB8E8] shrink-0" strokeWidth={2} />
            <div className="min-w-0">
              <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#94A3B8]">
                {isJa ? "電話" : "Phone"}
              </div>
              <a
                href="tel:+81468747156"
                className="text-[13px] text-white font-semibold tracking-tight hover:text-[#7AB8E8] transition-colors"
              >
                +81 046-874-7156
              </a>
              <span className="ml-2 text-[10.5px] text-[#94A3B8]">
                {isJa ? "平日 9–18 JST" : "Mon–Fri 9–18 JST"}
              </span>
            </div>
          </li>
          <li className="flex items-start gap-2.5" data-testid="footer-contact-email">
            <Mail className="h-3.5 w-3.5 mt-1 text-[#7AB8E8] shrink-0" strokeWidth={2} />
            <div className="min-w-0">
              <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#94A3B8]">
                {isJa ? "メール" : "Email"}
              </div>
              <a
                href="mailto:support@nojoenterprise.com"
                className="block text-[12.5px] text-white font-semibold tracking-tight hover:text-[#7AB8E8] transition-colors truncate"
              >
                support@nojoenterprise.com
              </a>
            </div>
          </li>
          <li data-testid="footer-contact-office">
            <Link
              to={`/${lang}/contact#office`}
              data-testid="footer-contact-office-link"
              className="group flex items-start gap-2.5 -m-2 p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <MapPin className="h-3.5 w-3.5 mt-1 text-[#7AB8E8] shrink-0" strokeWidth={2} />
              <div className="min-w-0">
                <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#94A3B8]">
                  {isJa ? "本社" : "Office"}
                </div>
                <span className="text-[13px] text-white font-semibold tracking-tight group-hover:text-[#7AB8E8] transition-colors">
                  {isJa ? "神奈川県横須賀市" : "Yokosuka, Kanagawa"}
                </span>
                <span className="ml-2 text-[10.5px] text-[#94A3B8]">
                  {isJa ? "2013年創業" : "Est. 2013"}
                </span>
                <ArrowUpRight
                  className="inline-block h-3 w-3 ml-1 align-middle opacity-50 group-hover:opacity-100 group-hover:-translate-y-px transition-all text-[#7AB8E8]"
                  strokeWidth={2.25}
                />
                <div className="text-[10px] text-[#94A3B8] mt-0.5">
                  {isJa ? "ストリートビューを見る" : "View street view"}
                </div>
              </div>
            </Link>
          </li>
        </ul>

        {/* ===== ROW 3 — disclaimer (single tight band) ===== */}
        <div className="pt-5">
          <p
            data-testid="footer-independent-notice"
            className="text-[10.5px] leading-[1.7] text-[#94A3B8] max-w-3xl"
          >
            {f("independentNotice")}
          </p>
        </div>

        <div className="mt-3 flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between text-[11px] text-[#94A3B8] tracking-tight">
          <span data-testid="footer-copyright" className="text-white font-semibold">
            © 2013–{year} {SITE.legalNameEn} · {f("rights")}
          </span>
          <span data-testid="footer-legal-line">
            {isJa ? (
              <>
                {SITE.legalNameJa} · 適格請求書発行事業者登録番号 {SITE.invoiceRegistration}
              </>
            ) : (
              <>
                Invoice Registration No. {SITE.invoiceRegistration} · {SITE.locationEn}
              </>
            )}
          </span>
        </div>
      </div>
    </footer>
  );
}
